import { v4 as uuidv4 } from 'uuid';
import { BIMElement, Family, FamilySymbol } from './types';
import { ParametricGeometryGenerator } from './families/ParametricGeometryGenerator';

export interface FamilyDefinition {
    name: string;
    category: string;
    path: string; // Path to 3D asset or script
    params: Record<string, any>; // Default instance parameters
    typeParams: Record<string, any>; // Default type parameters
    formulas?: Record<string, string>; // Formulas for parameters
    nestedComponents?: {
        name: string;
        familyId: string;
        typeId?: string;
        instanceParams?: Record<string, string | number>;
        shared?: boolean;
        transform?: number[];
    }[];
}

export interface TypeDefinition {
    name: string;
    family: string;
    params: Record<string, any>;
}

// Simple arithmetic expression evaluator
class FormulaEvaluator {
    static evaluate(context: Record<string, number>, formula: string): number {
        try {
            // 1. Replace parameter names with values
            // Sort keys by length desc to avoid replacing substrings (e.g. "Width" inside "HalfWidth")
            const sortedKeys = Object.keys(context).sort((a, b) => b.length - a.length);

            let expression = formula;
            for (const key of sortedKeys) {
                // Use regex to replace whole words only to avoid partial matches
                const regex = new RegExp(`\\b${key}\\b`, 'g');
                expression = expression.replace(regex, context[key].toString());
            }

            // 2. Handle basic math functions
            expression = expression.replace(/\babs\b/g, 'Math.abs');
            expression = expression.replace(/\bround\b/g, 'Math.round');
            expression = expression.replace(/\bceil\b/g, 'Math.ceil');
            expression = expression.replace(/\bfloor\b/g, 'Math.floor');
            expression = expression.replace(/\bsqrt\b/g, 'Math.sqrt');
            expression = expression.replace(/\bpow\b/g, 'Math.pow');
            expression = expression.replace(/\bmin\b/g, 'Math.min');
            expression = expression.replace(/\bmax\b/g, 'Math.max');
            expression = expression.replace(/\bif\b/g, '(condition, t, f) => condition ? t : f'); // Hacky IF support for eval? No, Function ctor better.

            // 3. Evaluate safely
            // Note: In production, use a proper parser. For prototype, Function is safer than eval but still risky if input is uncontrolled.
            // We assume formula comes from trusted internal catalog or cleaned input.

            // Handle if(condition, val_true, val_false) logic:
            // BIM syntax: if(x > 5, 10, 20)
            // JS syntax: x > 5 ? 10 : 20
            // Simple string replacement for "if(" is hard. simpler to expose a helper in the scope.

            const scopeHeaders = Object.keys(Math).filter(k => typeof (Math as any)[k] === 'function').map(k => `const ${k} = Math.${k};`).join('\n');
            const customIf = `const if_func = (c, t, f) => c ? t : f;`;
            // Replace if(...) with if_func(...)
            expression = expression.replace(/\bif\s*\(/g, 'if_func(');

            const func = new Function(scopeHeaders + '\n' + customIf + '\n' + `return (${expression});`);
            const result = func();

            if (typeof result !== 'number' || isNaN(result)) return 0;
            return result;

        } catch (e) {
            console.warn(`Formula evaluation error for "${formula}":`, e);
            return 0;
        }
    }

    static resolveParameters(baseParams: Record<string, any>, formulas: Record<string, string>): Record<string, any> {
        const resolved = { ...baseParams };
        // Naive single-pass. For dependencies, we'd need a topological sort or multi-pass.
        // We'll do a simple 3-pass to cover basic chains (A->B->C)

        for (let pass = 0; pass < 3; pass++) {
            for (const [paramName, formula] of Object.entries(formulas)) {
                // Only evaluate if we have numeric context for vars
                resolved[paramName] = this.evaluate(resolved, formula);
            }
        }
        return resolved;
    }
}

export class FamilyManager {
    private families: Map<string, Family> = new Map();
    private symbols: Map<string, FamilySymbol> = new Map();

    constructor() {
        this.loadDefaultFamilies();
    }

    private loadDefaultFamilies() {
        // Wall Families
        this.registerFamily({
            name: 'Basic Wall',
            category: 'Walls',
            path: 'system/wall',
            params: {},
            typeParams: {
                function: 'Exterior',
                wrappingAtInserts: 'DoNotWrap',
                wrappingAtEnds: 'None'
            }
        });

        this.registerType({
            name: 'Generic - 200mm',
            family: 'Basic Wall',
            params: {
                thickness: 0.2,
                layers: [{ function: 'Structure', materialId: 'Generic', thickness: 0.2, wraps: false }]
            }
        });

        this.registerType({
            name: 'Interior - 135mm Partition',
            family: 'Basic Wall',
            params: {
                thickness: 0.135,
                layers: [
                    { function: 'Finish 1', materialId: 'Gypsum', thickness: 0.0125, wraps: true },
                    { function: 'Structure', materialId: 'Metal Stud', thickness: 0.092, wraps: false },
                    { function: 'Finish 1', materialId: 'Gypsum', thickness: 0.0125, wraps: true }
                ]
            }
        });

        // Door Families
        this.registerFamily({
            name: 'Single-Flush',
            category: 'Doors',
            path: 'system/door_single_flush',
            params: {
                'Frame Material': 'Wood',
                'Finish': 'Paint',
                'Width': 0.915, // Default
                'Height': 2.134,
                'FrameWidth': 0.05
            },
            typeParams: {
                'Fire Rating': 'None'
            },
            formulas: {
                'HalfWidth': 'Width / 2', // Test Formula
                'RoughOpeningWidth': 'Width + (FrameWidth * 2) + 0.02'
            }
        });

        this.registerType({
            name: '0915 x 2134mm',
            family: 'Single-Flush',
            params: {
                Width: 0.915,
                Height: 2.134,
                FrameWidth: 0.05
            }
        });

        // Nested-family demo (BIM nested family parameter mapping): a door
        // host carrying a Handle component whose Elevation maps to the host's
        // HandleHeight. Exercises FamilyManager.getNestedInstances.
        this.registerFamily({
            name: 'Door Handle',
            category: 'Doors',
            path: 'system/door_handle',
            params: { 'Finish': 'Stainless Steel' },
            typeParams: {}
        });

        this.registerFamily({
            name: 'Door with Handle',
            category: 'Doors',
            path: 'system/door_single_flush_handle',
            params: { 'Width': 0.915, 'Height': 2.134 },
            typeParams: { 'HandleHeight': 1.10 },
            nestedComponents: [
                {
                    name: 'Handle',
                    familyId: 'Door Handle',
                    typeId: 'Standard',
                    instanceParams: {
                        'Elevation': 'HandleHeight', // mapped to host param
                        'Finish': 'Stainless Steel'  // literal
                    }
                }
            ]
        });

        this.registerType({
            name: 'Standard',
            family: 'Door with Handle',
            params: { Width: 0.915, Height: 2.134, HandleHeight: 1.10 }
        });

        // Window Families
        this.registerFamily({
            name: 'Fixed',
            category: 'Windows',
            path: 'system/window_fixed',
            params: {
                'Frame Material': 'Aluminum',
                'Glass Type': 'Double Pane'
            },
            typeParams: {}
        });

        this.registerType({
            name: '0915 x 1830mm',
            family: 'Fixed',
            params: {
                width: 0.915,
                height: 1.830
            }
        });
    }

    registerFamily(def: FamilyDefinition): Family {
        const family: Family = {
            id: uuidv4(),
            name: def.name,
            category: def.category,
            path: def.path,
            nestedParams: def.typeParams,
            instanceParams: def.params,
            formulas: def.formulas,
            nestedComponents: def.nestedComponents,
            symbols: []
        };
        this.families.set(def.name, family);
        return family;
    }

    /**
     * Phase 3: Register a family from a JSON Schema
     */
    registerSchema(schema: any): Family {
        // Convert Schema to internal Family structure
        // 1. Extract params
        const instanceParams: Record<string, any> = {};
        const typeParams: Record<string, any> = {}; // Schema doesn't strictly separate them yet in the same way, but we have 'isInstance' flag
        const formulas: Record<string, string> = {};

        if (schema.parameters) {
            schema.parameters.forEach((p: any) => {
                if (p.isInstance) {
                    instanceParams[p.name] = p.defaultValue;
                } else {
                    typeParams[p.name] = p.defaultValue;
                }
            });
        }

        if (schema.constraints) {
            schema.constraints.forEach((c: any) => {
                formulas[c.targetParameter] = c.formula;
            });
        }

        const family: Family = {
            id: uuidv4(),
            name: schema.name,
            category: schema.category,
            path: 'generated/schema',
            nestedParams: typeParams,
            instanceParams: instanceParams,
            formulas: formulas,
            geometrySchema: schema,
            symbols: []
        };

        this.families.set(schema.name, family);

        // Register defined Types
        if (schema.types) {
            schema.types.forEach((t: any) => {
                this.registerType({
                    name: t.name,
                    family: schema.name,
                    params: t.values
                });
            });
        }

        return family;
    }

    registerType(def: TypeDefinition): FamilySymbol {
        const family = this.families.get(def.family);
        if (!family) {
            throw new Error(`Family ${def.family} not found`);
        }

        // Merge defaults with overrides: Type Defaults (nestedParams) + Instance Defaults (params) + Type Overrides (def.params)
        let resolvedParams = { ...family.nestedParams, ...(family.instanceParams || {}), ...def.params };

        // Apply formulas
        if (family.formulas) {
            resolvedParams = FormulaEvaluator.resolveParameters(resolvedParams, family.formulas);
        }

        const symbol: FamilySymbol = {
            id: uuidv4(),
            name: def.name,
            parameters: resolvedParams
        };

        family.symbols.push(symbol);
        this.symbols.set(`${def.family}:${def.name}`, symbol);

        return symbol;
    }

    getFamily(name: string): Family | undefined {
        return this.families.get(name);
    }

    /** True when a family with this name is registered. */
    hasFamily(name: string): boolean {
        return this.families.has(name);
    }

    /** True when `Family: Type` resolves to a registered symbol. */
    hasType(familyName: string, typeName: string): boolean {
        return this.symbols.has(`${familyName}:${typeName}`);
    }

    /**
     * Return the family with the given name, creating a minimal registry entry
     * if needed. Used by Edit Type when the document references a family string
     * that was never formally registered (freeform type names from tools).
     */
    ensureFamily(name: string, category: string): Family {
        let family = this.families.get(name);
        if (!family) {
            family = this.registerFamily({
                name,
                category,
                path: 'project/derived',
                params: {},
                typeParams: {}
            });
        }
        return family;
    }

    /** List every registered family, optionally filtered by category. */
    getAllFamilies(category?: string): Family[] {
        const result: Family[] = [];
        this.families.forEach(family => {
            if (category && family.category !== category) return;
            result.push(family);
        });
        return result;
    }

    getType(familyName: string, typeName: string): FamilySymbol | undefined {
        return this.symbols.get(`${familyName}:${typeName}`);
    }

    /**
     * Re-run a family's parameter formulas over a parameter bag. Used by
     * Edit Type: formula-driven outputs are read-only in the dialog and are
     * recomputed from user-edited inputs on commit.
     */
    applyFormulas(familyName: string, parameters: Record<string, any>): Record<string, any> {
        const family = this.families.get(familyName);
        if (!family || !family.formulas || Object.keys(family.formulas).length === 0) return parameters;
        return FormulaEvaluator.resolveParameters(parameters, family.formulas);
    }

    /** Parameter names whose values are formula-driven (read-only in Edit Type). */
    getFormulaTargets(familyName: string): Set<string> {
        const family = this.families.get(familyName);
        return new Set(Object.keys(family?.formulas ?? {}));
    }

    getAllTypes(category?: string): { family: string, type: string, symbol: FamilySymbol }[] {
        const result: { family: string, type: string, symbol: FamilySymbol }[] = [];
        this.families.forEach(family => {
            if (category && family.category !== category) return;
            family.symbols.forEach(symbol => {
                result.push({
                    family: family.name,
                    type: symbol.name,
                    symbol
                });
            });
        });
        return result;
    }

    /** All registered type names of a family (for Edit Type dropdowns). */
    getTypeNames(familyName: string): string[] {
        const family = this.families.get(familyName);
        if (!family) return [];
        return family.symbols.map(s => s.name);
    }

    /**
     * Edit Type core write API: replace the stored parameter bag of a type.
     * Type edits must NOT re-run formulas here; the dialog already resolved
     * formula-driven values, and re-solving would clobber user edits of
     * formula outputs (BIM semantics: outputs show computed values).
     */
    setTypeParameters(familyName: string, typeName: string, parameters: Record<string, any>): FamilySymbol | undefined {
        const symbol = this.symbols.get(`${familyName}:${typeName}`);
        if (!symbol) return undefined;
        symbol.parameters = { ...parameters };
        return symbol;
    }

    /** Merge a patch into a type's parameters without replacing the bag. */
    updateTypeParameters(familyName: string, typeName: string, patch: Record<string, any>): FamilySymbol | undefined {
        const symbol = this.symbols.get(`${familyName}:${typeName}`);
        if (!symbol) return undefined;
        symbol.parameters = { ...symbol.parameters, ...patch };
        return symbol;
    }

    /**
     * Rename a type. Re-keys the symbol map, updates the family's symbol list
     * and the symbol's name. Returns false when source or target already exists.
     * Note: element `type` strings referencing the old name are rewritten by
     * the caller (TypeProperties propagation), not here.
     */
    renameType(familyName: string, oldTypeName: string, newTypeName: string): boolean {
        const trimmed = newTypeName.trim();
        if (!trimmed || trimmed === oldTypeName) return false;
        const symbol = this.symbols.get(`${familyName}:${oldTypeName}`);
        if (!symbol) return false;
        if (this.symbols.has(`${familyName}:${trimmed}`)) return false;

        const family = this.families.get(familyName);
        this.symbols.delete(`${familyName}:${oldTypeName}`);
        symbol.name = trimmed;
        this.symbols.set(`${familyName}:${trimmed}`, symbol);
        if (family) {
            const idx = family.symbols.findIndex(s => s.id === symbol.id);
            if (idx >= 0) family.symbols[idx] = symbol;
        }
        return true;
    }

    /** Duplicate a type (deep-copied params). Registry-only; see typePropertiesService. */
    duplicateType(familyName: string, sourceTypeName: string, desiredName?: string): string | null {
        const source = this.symbols.get(`${familyName}:${sourceTypeName}`);
        if (!source) return null;
        const family = this.families.get(familyName);
        if (!family) return null;

        let newName = (desiredName ?? '').trim() || `${sourceTypeName} Copy`;
        if (this.symbols.has(`${familyName}:${newName}`)) {
            let counter = 1;
            while (this.symbols.has(`${familyName}:${newName} (${counter})`)) counter++;
            newName = `${newName} (${counter})`;
        }

        const copy: FamilySymbol = {
            id: uuidv4(),
            name: newName,
            parameters: JSON.parse(JSON.stringify(source.parameters))
        };
        family.symbols.push(copy);
        this.symbols.set(`${familyName}:${newName}`, copy);
        return newName;
    }

    /**
     * Register a brand-new type inside a family with the given parameters.
     * Returns false when the family/type name collides or family is missing.
     */
    createType(familyName: string, typeName: string, parameters: Record<string, any>): boolean {
        const family = this.families.get(familyName);
        const trimmed = typeName.trim();
        if (!family || !trimmed) return false;
        if (this.symbols.has(`${familyName}:${trimmed}`)) return false;

        const symbol: FamilySymbol = { id: uuidv4(), name: trimmed, parameters: { ...parameters } };
        family.symbols.push(symbol);
        this.symbols.set(`${familyName}:${trimmed}`, symbol);
        return true;
    }

    /**
     * Create an element instance from a type.
     * If the family has Shared Nested components, this returns multiple elements.
     */
    instantiateFamily(
        familyName: string,
        typeName: string,
        position: [number, number, number] = [0, 0, 0],
        instanceParams: Record<string, any> = {}
    ): BIMElement[] {
        const symbol = this.getType(familyName, typeName);
        if (!symbol) return [];

        const family = this.families.get(familyName);
        if (!family) return [];

        const elements: BIMElement[] = [];

        // 1. Create Host Element
        const hostId = uuidv4();
        const hostElement: any = {
            id: hostId,
            name: `${family.name} - ${symbol.name}`,
            type: `${family.name}: ${symbol.name}`,
            category: family.category,
            parameters: { ...symbol.parameters, ...instanceParams },
            geometryData: { position }
        };
        elements.push(hostElement);

        // 2. Process Nested Components
        if (family.nestedComponents) {
            for (const comp of family.nestedComponents) {
                if (comp.shared) {
                    // Start Recursive instantiation for Shared components
                    // We need to resolve params first
                    const resolvedChildParams: Record<string, any> = {};
                    if (comp.instanceParams) {
                        for (const [key, value] of Object.entries(comp.instanceParams)) {
                            if (typeof value === 'string' && hostElement.parameters[value] !== undefined) {
                                resolvedChildParams[key] = hostElement.parameters[value];
                            } else {
                                resolvedChildParams[key] = value;
                            }
                        }
                    }

                    // Shared component position (simplification: just add offset or use identity for now)
                    // In real engine, we'd apply the component's transform to the host's transform.
                    const childPos: [number, number, number] = [
                        position[0] + (comp.transform?.[12] || 0),
                        position[1] + (comp.transform?.[13] || 0),
                        position[2] + (comp.transform?.[14] || 0)
                    ];

                    const children = this.instantiateFamily(comp.familyId, comp.typeId || 'Standard', childPos, resolvedChildParams);

                    // Link children to host?
                    // Usually via "Host" parameter or group
                    children.forEach(c => {
                        if (!c.parameters) c.parameters = {};
                        c.parameters['HostId'] = hostId;
                    });

                    elements.push(...children);
                }
            }
        }

        return elements;
    }

    // Legacy helper (kept for compatibility)
    createInstance(familyName: string, typeName: string): Partial<BIMElement> | null {
        const results = this.instantiateFamily(familyName, typeName);
        return results.length > 0 ? results[0] : null;
    }

    /**
     * Get definitions for nested components for a given host type
     */
    getNestedInstances(familyName: string, typeName: string): { family: string, type: string, params: Record<string, any> }[] {
        const family = this.families.get(familyName);
        const hostSymbol = this.getType(familyName, typeName);

        if (!family || !hostSymbol || !family.nestedComponents) return [];

        return family.nestedComponents.map(comp => {
            // Resolve parameters: Default Nested Type Params + Mapped Instance Params from Host
            // For now, simple mapping: if instanceParams has a string value that matches a Host Param, use that value

            const resolvedParams: Record<string, any> = {};

            if (comp.instanceParams) {
                for (const [key, value] of Object.entries(comp.instanceParams)) {
                    if (typeof value === 'string' && hostSymbol.parameters[value] !== undefined) {
                        // It's a mapped parameter
                        resolvedParams[key] = hostSymbol.parameters[value];
                    } else {
                        // Literal value
                        resolvedParams[key] = value;
                    }
                }
            }

            return {
                family: comp.familyId, // This should be the Family Name normally, but interface says familyId. 
                // We'll treat familyId as Name for this mock
                type: comp.typeId || 'Standard',
                params: resolvedParams
            };
        });
    }
    /**
     * Generate 3D geometry for a specific family/type/instance configuration
     */
    generateGeometry(familyName: string, typeName: string, instanceParams: Record<string, any> = {}): THREE.Group | null {
        const family = this.families.get(familyName);
        if (!family) return null;

        const symbol = this.getType(familyName, typeName);
        if (!symbol) return null;

        // 1. Resolve Parameters
        // Base: Type Parameters
        let resolved = { ...symbol.parameters };

        // Overlay: Instance Parameters
        Object.assign(resolved, instanceParams);

        // Apply Formulas (Re-run for instance specifics if needed)
        // Ideally we run the dependency graph here. For now, simple pass.
        if (family.formulas) {
            resolved = FormulaEvaluator.resolveParameters(resolved, family.formulas);
        }

        // 2. Generate Geometry
        if (family.geometrySchema) {
            return ParametricGeometryGenerator.generate(family.geometrySchema, resolved, this.generateGeometry.bind(this));
        }

        // Fallback for legacy families? 
        // Currently legacy families don't generate geometry here, tools do it.
        return null;
    }
}

export const familyManager = new FamilyManager();
